import { useCallback, useMemo } from 'react';
import {
    SettingsPages,
    blogHomePath,
    logoutPath,
    studentProfilePath,
    studentSettingsPath,
} from '@lingoda/router';
import {
    BookOpen,
    CircledQuestionIcon24,
    CustomerSupportIcon24,
    ExitLogoutIcon24,
    PersonCircleIcon24,
    Settings,
    UserGroupGradientIcon24,
} from '@lingoda/ui/icons/lingodaIcons';
import { trans } from '@lingoda/i18n';
import { preventDefault } from '@lingoda/utils';
import { actionClick } from '@lingoda/analytics';
import { Box, colorsPalette, useUiEvent } from '@lingoda/ui';
import { useOwnReferralQuery } from '@lingoda/graphql';
import { useLogout } from '@lingoda/students';
import { getLang } from '@lingoda/env';
import { DixaMessenger } from '@lingoda/help-center';
import { isStudentProfilePageEnabled } from '@lingoda/feature-flags';
import { Divider, MenuItem, MenuItemLink } from '../MenuComponents';

interface Props {
    onClose?: () => void;
}

export const UserMenuItems = ({ onClose }: Props) => {
    const locale = useMemo(() => getLang(), []);
    const isBlogShown = locale !== 'ru';
    const addEvent = useUiEvent();
    const { data } = useOwnReferralQuery();
    const performLogout = useLogout();
    const clickSection = useCallback(
        (section: string) => () => {
            void addEvent(actionClick(section));
            onClose?.();
        },
        [addEvent, onClose],
    );

    return (
        <>
            {isStudentProfilePageEnabled() ? (
                <>
                    <MenuItemLink to={studentProfilePath()} onClick={clickSection('My profile')}>
                        <PersonCircleIcon24 sx={{ color: colorsPalette.blackAlpha[70] }} />
                        {trans('navigation.user.my-profile', {}, 'public-common')}
                    </MenuItemLink>
                    <MenuItemLink
                        to={studentSettingsPath()}
                        onClick={clickSection('Account Settings')}
                    >
                        <Settings sx={{ color: colorsPalette.blackAlpha[70] }} />
                        {trans('navigation.user.account-settings', {}, 'public-common')}
                    </MenuItemLink>
                </>
            ) : (
                <MenuItemLink to={studentSettingsPath()} onClick={clickSection('My profile')}>
                    <Settings sx={{ color: colorsPalette.blackAlpha[70] }} />
                    {trans('navigation.user.my-profile', {}, 'public-common')}
                </MenuItemLink>
            )}
            <Divider />
            <MenuItemLink
                target="_blank"
                rel="noreferrer"
                to={trans('faq.link', {}, 'public-common')}
                onClick={clickSection('FAQ')}
            >
                <CircledQuestionIcon24 sx={{ color: colorsPalette.blackAlpha[70] }} />
                {trans('navigation.user.faq', {}, 'public-common')}
            </MenuItemLink>
            {isBlogShown && (
                <MenuItemLink target="_self" to={blogHomePath()} onClick={clickSection('Blog')}>
                    <BookOpen sx={{ color: colorsPalette.blackAlpha[70] }} />
                    {trans('footer.blog', {}, 'public-common')}
                </MenuItemLink>
            )}
            {DixaMessenger.getIsDixaInitialized() && (
                <MenuItem
                    component={Box}
                    onClick={() => {
                        DixaMessenger.setWidgetOpen(true);
                        onClose?.();
                    }}
                >
                    <CustomerSupportIcon24 sx={{ color: colorsPalette.blackAlpha[70] }} />
                    {trans('open-help-center', {}, 'public-settings')}
                </MenuItem>
            )}
            <Divider />
            {!!data?.ownReferral && (
                <>
                    <MenuItemLink
                        to={studentSettingsPath({ page: SettingsPages.Referrals })}
                        onClick={clickSection('ReferalMenuEntry')}
                    >
                        <UserGroupGradientIcon24 />
                        {trans('settings-tab-referrals', {}, 'public-settings')}
                    </MenuItemLink>
                    <Divider />
                </>
            )}
            <MenuItemLink
                to={logoutPath()}
                onClick={preventDefault(() => {
                    clickSection('logout');
                    void performLogout();
                })}
            >
                <ExitLogoutIcon24 sx={{ color: colorsPalette.blackAlpha[70] }} />
                {trans('navigation.user.logout', {}, 'public-common')}
            </MenuItemLink>
        </>
    );
};
